import React, { useEffect, useState } from "react";
import Input from "../../../form/Input";
import Select from "../../../form/Select";
import useForm from "../../../../hooks/useForm";
import Button from "../../../form/Button";
import useMutation from "../../../../hooks/useMutation";
import { quizAddEdit } from "../../../../services/quizService";
import Checkbox from "../../../form/Checkbox";
import useFetch from "../../../../hooks/useFetch";
import { questionList } from "../../../../services/questionService";
import { useDebounce } from "../../../../hooks/useDebounce";
import { scholarshipList } from "../../../../services/scholarshipService";
import MultiselectDropdown from "../../../form/MultiselectDropdown";
import { removeDuplicateFromArray } from "../../../../helpers";

const validation = {
  title: { required: true, message: "Title is required" },
  numberOfAttempts: [
    {
      type: "number",
      errorType: "info",
      min: 1,
    },
  ],
};

const ManageQuiz = ({ getResponse = () => {}, close = () => {}, data }) => {
  const [scholarshipFilters, setScholarshipFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });
  const [scholarshipKeyword, setScholarshipkeyword] = useState("");
  const scholarshipSearch = useDebounce(scholarshipKeyword, 300);

  const [questionFilters, setQuestionFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });
  const [questionkeyword, setQuestionkeyword] = useState("");
  const questionSearch = useDebounce(questionkeyword, 300);

  useEffect(() => {
    if (data?._id) {
      setFieldsValue(data);
    }
  }, [data]);

  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    resetField,
    formValidate,
  } = useForm({
    validationRules: validation,
    initialState: {
      numberOfAttempts: 1,
      questions: [
        {
          question: undefined,
          questionField: undefined,
          answerType: undefined,
          correct: undefined,
        },
      ],
    },
  });

  const {
    data: questions,
    error: questionError,
    isLoading: questionLoading,
    updateData,
    isFetching,
    refetch,
  } = useFetch({
    dependencies: ["question", questionFilters, questionSearch],
    fetchFunction: async () => {
      const res = await questionList({
        ...questionFilters,
        ...(questionSearch && { keyword: questionSearch }),
      });
      if (res?.docs?.status === 200) {
        return {
          ...res?.docs,
          data: removeDuplicateFromArray([
            ...(questions?.data || []),
            ...res?.docs?.data,
          ]),
        };
      } else {
        if (questions?.data?.length > 0) {
          return questions;
        } else {
          return res;
        }
      }
    },
  });

  const {
    data: scholarships,
    error: scholarshipError,
    isLoading: scholarshipLoading,
    updateData: scholarshipUpdate,
    isFetching: scholarshipFetching,
    refetch: scholarshipRefetch,
  } = useFetch({
    dependencies: ["scholarship", scholarshipFilters, scholarshipSearch],
    fetchFunction: async () => {
      const res = await scholarshipList({
        ...scholarshipFilters,
        ...(scholarshipSearch && { keyword: scholarshipSearch }),
      });
      if (res?.docs?.status === 200) {
        return {
          ...res?.docs,
          data: removeDuplicateFromArray([
            ...(scholarships?.data || []),
            ...res?.docs?.data,
          ]),
        };
      } else {
        if (scholarships?.data?.length > 0) {
          return scholarships;
        } else {
          return res;
        }
      }
    },
  });

  const {
    mutate,
    error,
    isPending: isLoading,
  } = useMutation({
    mutationFn: async (val) => {
      return await quizAddEdit({
        ...val,
        isAttempt: val?.numberOfAttempts > 1 ? true : false,
        ...(data?._id && { id: data?._id }),
      });
    },
    onSuccess: (res) => {
      getResponse(res);
      return res;
    },
    onError: (error) => {
      console.log(error);
    },
  });

  function loadData(e, obj, setData = () => {}) {
    if (
      Math.ceil(e.target.scrollTop) + 20 >=
        e.target.scrollHeight - e.target.clientHeight &&
      obj?.metadata?.hasNextPage
    ) {
      setData((pre) => ({
        ...pre,
        page: obj?.metadata?.nextPage,
      }));
    }
  }

  return (
    <form onSubmit={(event) => handleSubmit(event, mutate)}>
      <div className="grid grid-cols-4 gap-x-4 gap-y-4">
        <div className="relative col-span-4">
          <Input
            label="Quiz Title"
            inputPlaceholder={"Enter Quiz Title"}
            inputName="title"
            value={values?.title || ""}
            onChange={handleChange}
            {...formValidate(errors, "title")}
          />
        </div>
        <div className="relative col-span-2">
          <MultiselectDropdown
            label={"Select question"}
            dropdownData={removeDuplicateFromArray(
              [
                ...(data?.questionDetails || [])?.map((item) => ({
                  name: item?.title,
                  value: item?._id,
                })),
                ...(questions?.data || [])?.map((q) => ({
                  name: q?.title,
                  value: q?._id,
                })),
              ],
              "value"
            )}
            values={values?.questionId}
            onChange={(val) => {
              handleChange({ target: { name: "questionId", value: val } });
            }}
            onScroll={(e) => {
              loadData(e, questions, setQuestionFilters);
            }}
          />
        </div>
        <div className="relative col-span-2">
          <Input
            label="Number of Attempts"
            inputPlaceholder={"Enter Number of Attempts"}
            inputName="numberOfAttempts"
            value={values?.numberOfAttempts || ""}
            onChange={handleChange}
            {...formValidate(errors, "numberOfAttempts")}
          />
        </div>
      </div>
      <div className="relative col-span-4 mt-5">
        <div className="flex items-center justify-center gap-4">
          <Button
            buttonType="submit"
            buttonLabel={data?._id ? "Update" : "Save"}
            buttonClasses={
              "!rounded-full !px-8 !h-12 !border !border-chestnut-rose !bg-transparent !text-chestnut-rose hover:!bg-chestnut-rose hover:!text-white"
            }
            buttonLabelClasses={"mx-auto"}
            {...(isLoading && {
              loading: true,
              buttonIconPosition: "left",
              isDisable: true,
            })}
          />
          <Button
            buttonLabel={"Cancel"}
            buttonClasses={
              "!rounded-full !px-8 !h-12 !border !border-chestnut-rose !bg-transparent !text-chestnut-rose hover:!bg-chestnut-rose hover:!text-white"
            }
            buttonLabelClasses={"mx-auto"}
            buttonFunction={close}
          />
        </div>
      </div>
    </form>
  );
};

export default ManageQuiz;
